/**
 * EntityDisambiguationCard — Refine-agent interrupt UI for ambiguous entities.
 *
 * Shown in the left panel when the backend returns needs_clarification.
 * Users pick one close match per term (or search the portfolio via `/`), then
 * Continue with selected_entities, or Skip with an empty selection.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ConfigProvider } from 'antd';
import { useTheme } from '../../../theme/ThemeContext';
import type { LookupCategory, LookupItem } from '../AskAsperAgentic.types';
import { useSlashLookup, replaceSlashTokenQuery } from '../SearchBar/useSlashLookup';
import LookupCategoryDropdown from '../SearchBar/LookupCategoryDropdown';
import LookupSuggestionDropdown from '../SearchBar/LookupSuggestionDropdown';

export interface EntityDisambiguationMatch {
    column_name: string;
    entity_name: string;
}

export interface EntityDisambiguationTerm {
    entity_name: string;
    close_matches: EntityDisambiguationMatch[];
}

export interface EntityDisambiguationCardProps {
    terms: EntityDisambiguationTerm[];
    /** Initial selections (e.g. from history). Keyed by ambiguous entity_name. */
    initialSelections?: Record<string, EntityDisambiguationMatch>;
    lookupItems?: LookupItem[];
    lookupCategories?: LookupCategory[];
    /** When true, selections + actions are locked (after Continue/Skip or history). */
    resolved?: boolean;
    onContinue?: (selected: EntityDisambiguationMatch[]) => void;
    onSkip?: () => void;
    onLoadLookups?: () => void;
    'data-test'?: string;
    'data-test-id'?: string;
}

function formatColumnLabel(columnName: string): string {
    return columnName
        .trim()
        .replace(/_/g, '-')
        .replace(/\s+/g, '-')
        .toLowerCase();
}

function matchKey(match: EntityDisambiguationMatch): string {
    return `${match.column_name}::${match.entity_name}`;
}

interface TermPortfolioSearchProps {
    termEntityName: string;
    lookupItems?: LookupItem[];
    lookupCategories?: LookupCategory[];
    disabled?: boolean;
    onPick: (item: LookupItem) => void;
    onClose?: () => void;
    onLoadLookups?: () => void;
    'data-test-id'?: string;
}

const TermPortfolioSearch: React.FC<TermPortfolioSearchProps> = ({
    termEntityName,
    lookupItems,
    lookupCategories,
    disabled,
    onPick,
    onClose,
    onLoadLookups,
    'data-test-id': dataTestId,
}) => {
    const [value, setValue] = useState('/');
    const [caretPos, setCaretPos] = useState(1);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const pendingCaretRef = useRef<number | null>(null);
    const hasLoadedRef = useRef(false);

    const slash = useSlashLookup(value, caretPos, lookupItems, {
        categories: lookupCategories,
    });

    useEffect(() => {
        inputRef.current?.focus();
        inputRef.current?.setSelectionRange(1, 1);
    }, []);

    useEffect(() => {
        if (pendingCaretRef.current == null || !inputRef.current) return;
        const pos = pendingCaretRef.current;
        pendingCaretRef.current = null;
        inputRef.current.setSelectionRange(pos, pos);
        setCaretPos(pos);
    }, []);

    const syncCaret = useCallback(() => {
        const el = inputRef.current;
        if (!el) return;
        setCaretPos(el.selectionStart ?? 0);
    }, []);

    const acceptSuggestion = useCallback(
        (index: number) => {
            const match = slash.suggestions[index];
            if (!match) return;
            const result = slash.accept(match.item);
            if (!result) return;
            onPick(result.item);
        },
        [slash, onPick],
    );

    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const next = e.target.value;
            // If user backspaced away the slash, close the portfolio search
            if (next === '' || next === '/') {
                if (next === '') {
                    onClose?.();
                    return;
                }
            }
            // Keep a leading slash so the slash-lookup token stays active.
            const normalized = next.startsWith('/') ? next : `/${next.replace(/^\/*/, '')}`;
            setValue(normalized);
            const nextCaret = e.target.selectionStart ?? normalized.length;
            setCaretPos(Math.min(nextCaret, normalized.length));
        },
        [onClose],
    );

    const handleFocus = useCallback(() => {
        if (!hasLoadedRef.current) {
            hasLoadedRef.current = true;
            onLoadLookups?.();
        }
    }, [onLoadLookups]);

    const containerRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!slash.isActive) return;
        const onPointerDown = (event: MouseEvent) => {
            const target = event.target as HTMLElement | null;
            if (!target) return;
            if (containerRef.current && (target === containerRef.current || containerRef.current.contains(target))) {
                return;
            }
            slash.close();
            onClose?.();
        };
        document.addEventListener('mousedown', onPointerDown);
        return () => document.removeEventListener('mousedown', onPointerDown);
    }, [slash.isActive, slash.close, onClose]);

    const updateSlashQuery = useCallback(
        (query: string) => {
            const result = replaceSlashTokenQuery(value, slash.range, query);
            if (!result) return;
            const { nextValue, nextCaret } = result;
            setValue(nextValue);
            pendingCaretRef.current = nextCaret;
            setCaretPos(nextCaret);
        },
        [slash.range, value],
    );

    const handleLookupSearchKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                slash.setHighlightIndex(slash.highlightIndex + 1);
                return;
            }
            if (e.key === 'ArrowUp') {
                e.preventDefault();
                slash.setHighlightIndex(slash.highlightIndex - 1);
                return;
            }
            if (e.key === 'Enter') {
                e.preventDefault();
                if (slash.suggestions.length > 0) {
                    acceptSuggestion(slash.highlightIndex);
                }
                return;
            }
            if (e.key === 'Escape') {
                e.preventDefault();
                slash.goBack();
            }
        },
        [slash, acceptSuggestion],
    );

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (!slash.isActive) return;
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                slash.setHighlightIndex(slash.highlightIndex + 1);
                return;
            }
            if (e.key === 'ArrowUp') {
                e.preventDefault();
                slash.setHighlightIndex(slash.highlightIndex - 1);
                return;
            }
            if (e.key === 'Enter' || e.key === 'Tab') {
                e.preventDefault();
                const result = slash.activateHighlight();
                if (result?.kind === 'value') {
                    onPick(result.item);
                }
                return;
            }
            if (e.key === 'Escape') {
                e.preventDefault();
                if (slash.mode === 'search') {
                    slash.goBack();
                } else {
                    slash.close();
                    onClose?.();
                }
            }
        },
        [slash, onPick, onClose],
    );

    return (
        <div ref={containerRef} className="relative mt-2" data-test-id={dataTestId}>
            {slash.isActive && slash.mode === 'category' ? (
                <LookupCategoryDropdown
                    scoredCategories={slash.scoredCategories}
                    matchingValues={slash.matchingValues}
                    highlightIndex={slash.highlightIndex}
                    onHighlight={slash.setHighlightIndex}
                    onSelectCategory={(id) => slash.pickCategory(id)}
                    onSelectValue={acceptSuggestion}
                    query={slash.query}
                    hasNoMatches={slash.hasNoMatches}
                    contextLabel={slash.activeDimensionGroupLabel}
                    onBack={
                        slash.activeDimensionGroupId != null ? slash.goBack : undefined
                    }
                    className="!absolute !bottom-full !top-auto !mb-2 !mt-0 left-0 right-0 !z-50"
                    data-test-id={dataTestId ? `${dataTestId}-category` : undefined}
                />
            ) : (
                <LookupSuggestionDropdown
                    suggestions={slash.isActive ? slash.suggestions : []}
                    highlightIndex={slash.highlightIndex}
                    onHighlight={slash.setHighlightIndex}
                    onSelect={acceptSuggestion}
                    categoryLabel={slash.activeCategoryLabel}
                    query={slash.query}
                    onQueryChange={updateSlashQuery}
                    onSearchKeyDown={handleLookupSearchKeyDown}
                    onBack={slash.goBack}
                    className="!absolute !bottom-full !top-auto !mb-2 !mt-0 left-0 right-0 !z-50"
                    data-test-id={dataTestId ? `${dataTestId}-suggestions` : undefined}
                />
            )}
            <input
                ref={inputRef}
                type="text"
                value={value}
                disabled={disabled}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onKeyUp={syncCaret}
                onClick={syncCaret}
                onSelect={syncCaret}
                onFocus={handleFocus}
                aria-label={`Search portfolio for ${termEntityName}`}
                className="w-full rounded-lg border border-[#e8eafa] bg-white px-3 py-2 font-['Open_Sans'] text-[14px] leading-5 text-[rgba(0,0,0,0.88)] outline-none focus:border-[#3B2C77] focus:shadow-[0_0_0_2px_rgba(59,44,119,0.2)]"
                data-test-id={dataTestId ? `${dataTestId}-input` : undefined}
            />
            <p className="mt-1.5 font-['Open_Sans'] text-[12px] leading-4 text-[#A1A1AA]">
                Pick an entity type, or select a value directly.
            </p>
        </div>
    );
};

const EntityDisambiguationCard: React.FC<EntityDisambiguationCardProps> = ({
    terms,
    initialSelections,
    lookupItems,
    lookupCategories,
    resolved = false,
    onContinue,
    onSkip,
    onLoadLookups,
    'data-test': dataTest,
    'data-test-id': dataTestId = 'entity-disambiguation-card',
}) => {
    const theme = useTheme();
    const [selections, setSelections] = useState<Record<string, EntityDisambiguationMatch>>(
        () => initialSelections ?? {},
    );
    /** Portfolio picks (and history selections) that aren't in the original close_matches. */
    const [extraMatches, setExtraMatches] = useState<Record<string, EntityDisambiguationMatch[]>>(
        () => {
            const seed: Record<string, EntityDisambiguationMatch[]> = {};
            if (!initialSelections) return seed;
            for (const term of terms) {
                const picked = initialSelections[term.entity_name];
                if (!picked) continue;
                const alreadyListed = (term.close_matches ?? []).some(
                    (m) => matchKey(m) === matchKey(picked),
                );
                if (!alreadyListed) seed[term.entity_name] = [picked];
            }
            return seed;
        },
    );
    const [portfolioOpenFor, setPortfolioOpenFor] = useState<string | null>(null);
    const [expandedTerms, setExpandedTerms] = useState<Set<string>>(
        () => new Set(terms.map((t) => t.entity_name)),
    );
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (initialSelections) {
            setSelections(initialSelections);
            setExtraMatches((prev) => {
                const next = { ...prev };
                for (const term of terms) {
                    const picked = initialSelections[term.entity_name];
                    if (!picked) continue;
                    const listed = [
                        ...(term.close_matches ?? []),
                        ...(next[term.entity_name] ?? []),
                    ];
                    if (listed.some((m) => matchKey(m) === matchKey(picked))) continue;
                    next[term.entity_name] = [...(next[term.entity_name] ?? []), picked];
                }
                return next;
            });
        }
    }, [initialSelections, terms]);

    useEffect(() => {
        if (resolved) setIsSubmitting(false);
    }, [resolved]);

    const termCount = terms.length;
    const allSelected = useMemo(
        () => terms.length > 0 && terms.every((t) => selections[t.entity_name] != null),
        [terms, selections],
    );
    const canContinue = !resolved && allSelected;

    const matchesForTerm = useCallback(
        (term: EntityDisambiguationTerm): EntityDisambiguationMatch[] => {
            const base = term.close_matches ?? [];
            const extras = extraMatches[term.entity_name] ?? [];
            if (extras.length === 0) return base;
            const seen = new Set(base.map(matchKey));
            const merged = [...base];
            for (const extra of extras) {
                const key = matchKey(extra);
                if (seen.has(key)) continue;
                seen.add(key);
                merged.push(extra);
            }
            return merged;
        },
        [extraMatches],
    );

    const selectMatch = useCallback(
        (termName: string, match: EntityDisambiguationMatch) => {
            if (resolved) return;
            setSelections((prev) => ({ ...prev, [termName]: match }));
            setPortfolioOpenFor((open) => (open === termName ? null : open));
            setExpandedTerms((prev) => {
                const next = new Set(prev);
                next.delete(termName);
                return next;
            });
        },
        [resolved],
    );

    const toggleTerm = useCallback((termName: string) => {
        setExpandedTerms((prev) => {
            const next = new Set(prev);
            if (next.has(termName)) {
                next.delete(termName);
            } else {
                next.add(termName);
            }
            return next;
        });
    }, []);

    const handlePortfolioPick = useCallback(
        (termName: string, item: LookupItem) => {
            const match: EntityDisambiguationMatch = {
                column_name: item.column_name,
                entity_name: item.entity_name,
            };
            // Surface the portfolio choice in the option list so the user can
            // see what we're continuing with (selected state).
            setExtraMatches((prev) => {
                const existing = prev[termName] ?? [];
                const term = terms.find((t) => t.entity_name === termName);
                const alreadyListed = [
                    ...(term?.close_matches ?? []),
                    ...existing,
                ].some((m) => matchKey(m) === matchKey(match));
                if (alreadyListed) return prev;
                return { ...prev, [termName]: [...existing, match] };
            });
            selectMatch(termName, match);
            setPortfolioOpenFor(null);
        },
        [selectMatch, terms],
    );

    const handleContinue = useCallback(() => {
        if (!canContinue || isSubmitting) return;
        setIsSubmitting(true);
        const selected = terms
            .map((t) => selections[t.entity_name])
            .filter((m): m is EntityDisambiguationMatch => m != null);
        onContinue?.(selected);
    }, [canContinue, isSubmitting, terms, selections, onContinue]);

    const handleSkip = useCallback(() => {
        if (resolved) return;
        onSkip?.();
    }, [resolved, onSkip]);

    return (
        <ConfigProvider theme={theme}>
            <div
                className="bg-white rounded-xl border border-[#e8eafa]"
                data-test={dataTest}
                data-test-id={dataTestId}
                data-entity-disambiguation-card
            >
                <div className="px-5 pt-5 pb-4 border-b border-[#e8eafa]">
                    <h3 className="m-0 font-['Open_Sans'] font-semibold text-[16px] leading-6 text-[rgba(0,0,0,0.88)]">
                        We couldn&apos;t find exact matches for {termCount} term
                        {termCount === 1 ? '' : 's'}
                    </h3>
                    <p className="mt-1 mb-0 font-['Open_Sans'] text-[14px] leading-5 text-[rgba(0,0,0,0.65)]">
                        Select the best match for each term to continue
                    </p>
                </div>

                <div className="px-5 py-4 space-y-3">
                    {terms.map((term) => {
                        const selected = selections[term.entity_name];
                        const portfolioOpen = portfolioOpenFor === term.entity_name;
                        const matches = matchesForTerm(term);
                        const isExpanded = expandedTerms.has(term.entity_name);

                        return (
                            <div
                                key={term.entity_name}
                                data-test-id={`${dataTestId}-term-${term.entity_name}`}
                                className="border border-[#e8eafa] rounded-lg"
                            >
                                <button
                                    type="button"
                                    disabled={resolved}
                                    onClick={() => toggleTerm(term.entity_name)}
                                    className={`w-full px-3 py-3 flex items-center justify-between gap-3 transition-colors ${
                                        isExpanded
                                            ? 'bg-[#f5f3ff]'
                                            : 'bg-white hover:bg-[#fafafa]'
                                    } ${resolved ? 'cursor-default' : 'cursor-pointer'}`}
                                    data-test-id={`${dataTestId}-term-header-${term.entity_name}`}
                                >
                                    <div className="flex items-center gap-2 flex-1 text-left min-w-0">
                                        <p className="m-0 font-['Open_Sans'] text-[12px] leading-4 tracking-[0.04em] text-[rgba(0,0,0,0.45)] uppercase shrink-0">
                                            Match for{' '}
                                        </p>
                                        <code className="font-mono normal-case tracking-normal text-[#3b2c77] bg-[#f1edff] px-1.5 py-0.5 rounded text-[12px] truncate">
                                            {term.entity_name}
                                        </code>
                                        {selected && (
                                            <span className="font-['Open_Sans'] text-[12px] leading-4 text-[#3b2c77] shrink-0">
                                                — {selected.entity_name}
                                            </span>
                                        )}
                                    </div>
                                    <svg
                                        className={`w-5 h-5 text-[rgba(0,0,0,0.45)] shrink-0 transition-transform duration-200 ${
                                            isExpanded ? 'rotate-180' : ''
                                        }`}
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M6 9l6 6 6-6"
                                        />
                                    </svg>
                                </button>

                                {isExpanded && (
                                    <div className="border-t border-[#e8eafa] px-3 py-3 space-y-2 bg-white">
                                        <div className="flex items-center justify-between gap-3 mb-2">
                                            <span className="font-['Open_Sans'] text-[12px] leading-4 text-[rgba(0,0,0,0.45)] shrink-0">
                                                Showing top {matches.length} match
                                                {matches.length === 1 ? '' : 'es'}
                                            </span>
                                        </div>

                                        {matches.map((match) => {
                                            const isSelected =
                                                selected != null && matchKey(selected) === matchKey(match);
                                            return (
                                                <button
                                                    key={matchKey(match)}
                                                    type="button"
                                                    disabled={resolved}
                                                    onClick={() => selectMatch(term.entity_name, match)}
                                                    className={`w-full flex items-center justify-between gap-3 rounded-lg border px-3 py-2 text-left transition-colors ${
                                                        isSelected
                                                            ? 'border-[#3B2C77] bg-[#f5f3ff]'
                                                            : 'border-[#e8eafa] bg-white hover:bg-[#fafafa]'
                                                    } ${resolved ? 'cursor-default opacity-90' : 'cursor-pointer'}`}
                                                    data-test-id={`${dataTestId}-match-${term.entity_name}-${match.entity_name}`}
                                                >
                                                    <span className="font-['Open_Sans'] text-[14px] leading-5 text-[rgba(0,0,0,0.88)] truncate">
                                                        {match.entity_name}
                                                    </span>
                                                    <span className="font-['Open_Sans'] text-[12px] leading-4 text-[#3b2c77] bg-[#f1edff] rounded-md px-2 py-0.5 shrink-0 whitespace-nowrap">
                                                        {formatColumnLabel(match.column_name)}
                                                    </span>
                                                </button>
                                            );
                                        })}

                                        {!resolved && (
                                            <div className="mt-3 pt-2 border-t border-[#e8eafa]">
                                                {portfolioOpen ? (
                                                    <TermPortfolioSearch
                                                        termEntityName={term.entity_name}
                                                        lookupItems={lookupItems}
                                                        lookupCategories={lookupCategories}
                                                        onPick={(item) =>
                                                            handlePortfolioPick(term.entity_name, item)
                                                        }
                                                        onClose={() => setPortfolioOpenFor(null)}
                                                        onLoadLookups={onLoadLookups}
                                                        data-test-id={`${dataTestId}-portfolio-${term.entity_name}`}
                                                    />
                                                ) : (
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setPortfolioOpenFor(term.entity_name);
                                                            onLoadLookups?.();
                                                        }}
                                                        className="font-['Open_Sans'] text-[14px] leading-5 text-[#3b2c77] hover:text-[#2a1f54] bg-transparent border-0 p-0 cursor-pointer"
                                                        data-test-id={`${dataTestId}-search-portfolio-${term.entity_name}`}
                                                    >
                                                        None of these — search portfolio
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                <div className="flex items-center justify-between gap-3 px-5 py-4 border-t border-[#e8eafa]">
                    <button
                        type="button"
                        disabled={resolved}
                        onClick={handleSkip}
                        className={`font-['Open_Sans'] text-[14px] leading-5 bg-transparent border-0 p-0 ${
                            resolved
                                ? 'text-[rgba(0,0,0,0.25)] cursor-default'
                                : 'text-[rgba(0,0,0,0.65)] hover:text-[rgba(0,0,0,0.88)] cursor-pointer'
                        }`}
                        data-test-id={`${dataTestId}-skip`}
                    >
                        Skip
                    </button>
                    <button
                        type="button"
                        disabled={!canContinue || isSubmitting}
                        onClick={handleContinue}
                        className={`font-['Open_Sans'] text-[14px] leading-5 font-normal rounded px-3 py-1 transition-colors ${
                            canContinue && !isSubmitting
                                ? 'bg-[#3B2C77] text-[#fbfaff] hover:bg-gray-800 cursor-pointer'
                                : 'bg-black/50 text-[#fbfaff] cursor-not-allowed'
                        }`}
                        data-test-id={`${dataTestId}-continue`}
                    >
                        Continue
                    </button>
                </div>
            </div>
        </ConfigProvider>
    );
};

export default EntityDisambiguationCard;
